import { AfterViewInit, Component, ElementRef, HostListener, Inject, Input, OnDestroy, Renderer2 } from '@angular/core';
import { CurrentSwipeMenu } from './current.swipe-menu';
import { SwipeMenuService } from './swipe-menu.service';
import { v4 as uuidv4 } from 'uuid';

export type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';

export class CurrentSwipeMenuImpl implements CurrentSwipeMenu {
  private swipeMenu: SwipeMenuComponent | undefined;

  setSwipeMenu(swipeMenu: SwipeMenuComponent): void {
    this.swipeMenu = swipeMenu;
  }

  close(): void {
    this.swipeMenu?.closeMenu();
  }
}

@Component({
  selector: 'app-swipe-menu',
  templateUrl: './swipe-menu.component.html',
  styleUrls: ['./swipe-menu.component.scss'],
  providers: [
    { provide: CurrentSwipeMenu, useClass: CurrentSwipeMenuImpl }
  ]
})
export class SwipeMenuComponent implements AfterViewInit, OnDestroy {

  currentId: string = uuidv4();

  @Input()
  public set id(value: string) {
    if (value && value !== this.currentId) {
      this.swipeMenuService.deregister(this.currentId);
      this.currentId = value;
      this.swipeMenuService.register(this.currentId, this);
    }
  }

  @Input()
  public stickOutSize: number = 100;
  @Input()
  public menuPosition: Direction = 'DOWN';
  isOpened = false;

  openPos: number = 0;
  closedPos: number = 0;
  currentPos: number = 0;
  lastCurrentPos: number = 0;

  swipeThreshold = 20; // default 20px
  start?: number = undefined;
  current?: number = undefined;
  isOpening?: boolean = undefined;

  constructor(private menu: ElementRef, private renderer: Renderer2,
              @Inject(CurrentSwipeMenu) private currentSwipeMenu: CurrentSwipeMenuImpl,
              private swipeMenuService: SwipeMenuService) {
    currentSwipeMenu.setSwipeMenu(this);
    this.swipeMenuService.register(this.currentId, this);
  }

  ngAfterViewInit(): void {
    this.calcStates();

    this.closeMenu();
  }

  calcStates(): void {
    if (this.menuPosition === 'DOWN') {
      this.openPos = 0;
      this.closedPos = window.innerHeight - this.stickOutSize;
    } else if (this.menuPosition === 'RIGHT') {
      this.openPos = 0;
      this.closedPos = window.innerWidth - this.stickOutSize;
    } else if (this.menuPosition === 'UP') {
      this.openPos = 0;
      this.closedPos = -window.innerHeight + this.stickOutSize;
    } else if (this.menuPosition === 'LEFT') {
      this.openPos = 0;
      this.closedPos = -window.innerWidth + this.stickOutSize;
    }
  }

  isVertical(): boolean {
    return this.menuPosition === 'UP' || this.menuPosition === 'DOWN';
  }

  draw(): void {
    const translateFunction = this.isVertical() ? 'translateY' : 'translateX';

    let pos = Math.min(Math.max(this.openPos, this.currentPos), this.closedPos);
    if (this.openPos > this.closedPos) {
      pos = Math.max(Math.min(this.openPos, this.currentPos), this.closedPos);
    }

    this.renderer.setStyle(this.menu!.nativeElement, 'transform', translateFunction + '(' + pos + 'px)');
  }

  @HostListener('touchstart', ['$event'])
  handleTouchStart(e: TouchEvent): void {
    this.lastCurrentPos = this.currentPos;

    this.start = e.touches[0].clientX;
    if (this.isVertical()) {
      this.start = e.touches[0].clientY;
    }

    this.current = this.start;
    this.isOpening = false;

    e.cancelBubble = true;
  }

  @HostListener('touchmove', ['$event'])
  handleTouchMove(e: TouchEvent): void {
    if (!this.start || !this.current) {
      return;
    }

    let newPos = e.touches[0].clientX;
    if (this.isVertical()) {
      newPos = e.touches[0].clientY;
    }

    const diffToLastPos = newPos - this.current;
    this.isOpening = this.checkIfOpening(diffToLastPos);
    this.current = newPos;

    if (this.isTriggering()) {
      this.currentPos = this.lastCurrentPos + this.current - this.start;
      this.draw();
    }

    e.cancelBubble = true;
  }

  @HostListener('touchend', ['$event'])
  handleTouchEnd(e: TouchEvent): void {
    if (this.isTriggering()) {
      if (this.isOpening) {
        this.openMenu();
      } else {
        this.closeMenu();
      }
    }
    this.start = undefined;
    this.current = undefined;
    this.isOpening = undefined;

    e.cancelBubble = true;
  }

  checkIfOpening(diffToLastPos: number): boolean {
    if (this.menuPosition === 'DOWN' || this.menuPosition === 'RIGHT') {
      return diffToLastPos < 0;
    }
    return diffToLastPos > 0;
  }

  onHeaderButtonClick(): void {
    if (this.isOpened) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu(): void {
    this.currentPos = this.openPos;
    this.draw();
    this.isOpened = true;
  }

  closeMenu(): void {
    this.currentPos = this.closedPos;
    this.draw();
    this.isOpened = false;
  }

  isTriggering(): boolean {
    if (!this.start || !this.current) {
      return false;
    }

    const yDiff = this.current - this.start;
    return Math.abs(yDiff) > this.swipeThreshold;
  }

  @HostListener('window:resize')
  onResize(): void {
    // wait tick for mobile devices' orientation change, why? I don't know
    setTimeout(() => {
      this.calcStates();

      if (this.isOpened) {
        this.openMenu();
      } else {
        this.closeMenu();
      }
    }, 200);
  }

  ngOnDestroy(): void {
    this.swipeMenuService.deregister(this.id);
  }
}
